import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent } from "@workspace/amarmeds-ds/components/ui/card";

import { api, ApiError, riderScreenQuery, type RiderScreen, type RiderStop } from "../api";
import { bnDigits, takaBn } from "../format";

// The rider's phone screen, Bangla first, big targets for one thumb in sunlight.
// Top: progress, cash held against the limit, and a line to ops. Then any bag
// that has to go back to the pharmacy. Then the run: pickup, the next stop open
// (call, map, the customer's code, cash), the rest one line each, finished ones
// folded. Between runs: how the last one went and the cash to hand in.

const T = {
  noRun: "এখন কোনো রান নেই। নতুন রান এলে শব্দ হবে এবং এখানে দেখাবে।",
  pickupAt: "ফার্মেসি থেকে নিন",
  run: (n: number) => `রান ${bnDigits(n)}`,
  bags: (n: number) => `${bnDigits(n)}টি ব্যাগ`,
  items: (n: number) => `${bnDigits(n)}টি ওষুধ`,
  pickedUp: "সব ব্যাগ গুনে নিয়েছি",
  tickBags: "প্রতিটি ব্যাগের নম্বর মিলিয়ে টিক দিন",
  progress: (d: number, t: number) => `${bnDigits(d)} / ${bnDigits(t)} ডেলিভারি`,
  cashHeld: "হাতে টাকা",
  limit: (p: number) => `সীমা ${takaBn(p)}`,
  overLimit: "সীমার বেশি টাকা হাতে। অপসে জমা দিন।",
  callOps: "অপসকে কল করুন",
  next: "পরের স্টপ",
  later: (n: number) => `পরে · ${bnDigits(n)}`,
  doneList: (n: number) => `হয়ে গেছে · ${bnDigits(n)}`,
  collect: "নিতে হবে",
  paid: "টাকা দেওয়া আছে",
  by: (t: string) => `${t} এর মধ্যে`,
  late: "সময় পার হয়ে গেছে",
  code: "গ্রাহকের ৪ সংখ্যার কোড",
  holdCash: (p: number) => `ধরে রাখুন: ${takaBn(p)} বুঝে পেয়েছি`,
  deliver: "ডেলিভারি সম্পন্ন",
  sending: "পাঠানো হচ্ছে…",
  cantDeliver: "ডেলিভারি করা যায়নি",
  callFirst: "আগে গ্রাহককে কল করুন, তারপর কারণ বাছুন।",
  confirmFail: (id: string, why: string) => `${id}: "${why}" — নিশ্চিত? ব্যাগটি ফার্মেসিতে ফেরত দিতে হবে।`,
  back: "ফিরে যান",
  call: "কল করুন",
  map: "ম্যাপ",
  locked: "ভুল কোড বেশি বার দেওয়া হয়েছে। অপসকে কল করুন।",
  returnTitle: (id: string, why: string) => `${id} ${why === "cancelled" ? "বাতিল হয়েছে" : "ডেলিভারি হয়নি"}`,
  returnBody: "ব্যাগটি ফার্মেসিতে ফেরত দিন",
  returned: "ফেরত দিয়েছি",
  confirmReturn: (id: string) => `${id} এর ব্যাগ ফার্মেসিকে বুঝিয়ে দিয়েছেন?`,
  lastRun: (n: number) => `রান ${bnDigits(n)} শেষ`,
  summary: (d: number, f: number, c: number) => `${bnDigits(d)}টি ডেলিভারি · ${bnDigits(f)}টি হয়নি${c ? ` · ${bnDigits(c)}টি বাতিল` : ""}`,
  collected: "এই রানে টাকা নেওয়া",
  handIn: "অপসে জমা দিতে হবে",
  reasons: [
    ["not_home", "বাসায় কেউ নেই"],
    ["wrong_address", "ঠিকানা ভুল"],
    ["refused", "নিতে চায়নি"],
    ["unreachable", "ফোন ধরছে না"],
    ["other", "অন্য কারণ"],
  ] as const,
};

/** Server answers in Bangla, so the rider never sees a raw code. */
function riderError(err: unknown): string {
  if (!(err instanceof ApiError)) return "নেটওয়ার্ক সমস্যা। আবার চেষ্টা করুন।";
  switch (err.code) {
    case "wrong_code":
      return `ভুল কোড। আর ${bnDigits(Number(err.body.triesLeft ?? 0))} বার চেষ্টা করা যাবে।`;
    case "code_locked":
      return T.locked;
    case "cash_required":
      return "টাকা বুঝে পেয়েছেন কিনা নিশ্চিত করুন।";
    case "not_out_for_delivery":
    case "order_changed":
      return "অর্ডারটি বদলে গেছে (অপস হয়তো বাতিল করেছে)। পাতা নতুন করে দেখানো হচ্ছে।";
    case "order_not_found":
    case "run_not_found":
      return "এই অর্ডার আর আপনার রানে নেই।";
    case "already_picked_up":
      return "ব্যাগ আগেই নেওয়া হয়েছে।";
    case "ordering_unavailable":
      return "সার্ভারে সমস্যা। অপসকে কল করুন।";
    case "nothing_to_return":
      return "এটি আগেই ফেরত দেওয়া হয়েছে।";
    default:
      return "কিছু একটা ঠিক হয়নি। আবার চেষ্টা করুন বা অপসকে কল করুন।";
  }
}

const dhakaClock = (iso: string) => new Date(iso).toLocaleTimeString("bn-BD", { timeZone: "Asia/Dhaka", hour: "numeric", minute: "2-digit" });
const mapUrl = (address: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address}, Dhaka`)}`;

// Whether the rider has tapped Call for an order: a failed delivery needs a call first.
const CALLED_KEY = "savon-rider-called";
function calledSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CALLED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
function markCalled(orderId: string) {
  const s = calledSet();
  s.add(orderId);
  try {
    localStorage.setItem(CALLED_KEY, JSON.stringify([...s].slice(-100)));
  } catch {
    // Private mode: the tap still counts until the page reloads.
  }
}

export default function RiderRun() {
  const qc = useQueryClient();
  const screen = useQuery({ ...riderScreenQuery, refetchInterval: 15_000, refetchIntervalInBackground: true });
  const refresh = () => qc.invalidateQueries({ queryKey: ["run"] });

  if (screen.isLoading) return <p className="text-lg">…</p>;
  if (!screen.data) return <p className="text-lg text-destructive">{riderError(screen.error)}</p>;
  const s = screen.data;
  const r = s.run;

  return (
    <div className="space-y-3 text-lg">
      <TopStrip screen={s} />
      {s.returns.map((ret) => (
        <ReturnBanner key={`${ret.orderId}-${ret.runId}`} item={ret} onDone={refresh} />
      ))}
      {!r && <BetweenRuns screen={s} />}
      {r && r.status === "assigned" && <Pickup run={r} onDone={refresh} />}
      {r && r.status !== "assigned" && <Stops stops={r.stops} opsPhone={s.opsPhone} onDone={refresh} />}
    </div>
  );
}

function TopStrip({ screen: s }: { screen: RiderScreen }) {
  const r = s.run;
  const total = r?.stops.length ?? 0;
  const delivered = r?.stops.filter((x) => x.status === "delivered").length ?? 0;
  const over = s.cash.heldPaisa > s.cash.limitPaisa;
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            {r ? (
              <>
                <div className="truncate text-sm text-muted-foreground">
                  {T.run(r.runId)} · {r.pharmacy?.name ?? "—"}
                </div>
                <div className="font-semibold">{T.progress(delivered, total)}</div>
                <div className="mt-1 h-2 rounded bg-muted">
                  <div className="h-2 rounded bg-primary" style={{ width: total ? `${(delivered / total) * 100}%` : 0 }} />
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">SAVON রাইডার</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">{T.cashHeld}</div>
            <div className={`font-semibold ${over ? "text-destructive" : ""}`}>{takaBn(s.cash.heldPaisa)}</div>
            <div className="text-xs text-muted-foreground">{T.limit(s.cash.limitPaisa)}</div>
          </div>
        </div>
        {over && <p className="text-sm text-destructive">{T.overLimit}</p>}
        {s.opsPhone && (
          <a href={`tel:${s.opsPhone}`} className="block">
            <Button variant="outline" className="w-full">
              {T.callOps}
            </Button>
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function ReturnBanner({ item, onDone }: { item: RiderScreen["returns"][number]; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const done = useMutation({
    mutationFn: () => api(`/rider/returns/${item.orderId}`, { body: {} }),
    onSuccess: () => onDone(),
    onError: (err) => setError(riderError(err)),
  });
  return (
    <div className="space-y-2 rounded-lg border-2 border-amber-500 bg-amber-50 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-50">
      <div className="font-semibold">{T.returnTitle(item.orderId, item.why)}</div>
      <div>{T.returnBody}</div>
      <Button className="big-tap w-full" disabled={done.isPending} onClick={() => window.confirm(T.confirmReturn(item.orderId)) && done.mutate()}>
        {T.returned}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function BetweenRuns({ screen: s }: { screen: RiderScreen }) {
  const last = s.lastRun;
  return (
    <>
      {last && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="text-xl font-semibold">{T.lastRun(last.runId)}</div>
            <div>{T.summary(last.delivered, last.failed, last.cancelled)}</div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{T.collected}</span>
              <span className="font-semibold">{takaBn(last.cashCollectedPaisa)}</span>
            </div>
            {s.cash.heldPaisa > 0 && (
              <div className="flex justify-between text-xl">
                <span>{T.handIn}</span>
                <span className="font-bold">{takaBn(s.cash.heldPaisa)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <p>{T.noRun}</p>
    </>
  );
}

function Pickup({ run, onDone }: { run: NonNullable<RiderScreen["run"]>; onDone: () => void }) {
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const pickup = useMutation({
    mutationFn: () => api(`/rider/runs/${run.runId}/pickup`, { body: {} }),
    onSuccess: () => onDone(),
    onError: (err) => setError(riderError(err)),
  });
  const all = run.stops.every((x) => ticked.has(x.orderId));
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="text-sm text-muted-foreground">{T.pickupAt}</div>
        <div className="text-xl font-semibold">{run.pharmacy?.name ?? "—"}</div>
        <div>
          {T.bags(run.stops.length)} · {T.tickBags}
        </div>
        <div className="space-y-2">
          {run.stops.map((x) => (
            <label key={x.orderId} className="flex min-h-[56px] items-center gap-3 rounded-md border px-3">
              <input
                type="checkbox"
                className="size-7"
                checked={ticked.has(x.orderId)}
                onChange={(e) =>
                  setTicked((t) => {
                    const n = new Set(t);
                    if (e.target.checked) n.add(x.orderId);
                    else n.delete(x.orderId);
                    return n;
                  })
                }
              />
              <span className="text-xl font-semibold tracking-wide">{x.orderId}</span>
              <span className="ml-auto text-sm text-muted-foreground">{x.collectPaisa > 0 ? takaBn(x.collectPaisa) : T.paid}</span>
            </label>
          ))}
        </div>
        <Button className="big-tap w-full" disabled={!all || pickup.isPending} onClick={() => pickup.mutate()}>
          {T.pickedUp}
        </Button>
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function Stops({ stops, opsPhone, onDone }: { stops: RiderStop[]; opsPhone: string | null; onDone: () => void }) {
  const open = stops.filter((x) => x.status === "picked_up");
  const done = stops.filter((x) => x.status !== "picked_up");
  const [chosen, setChosen] = useState<string | null>(null);
  const current = open.find((x) => x.orderId === chosen) ?? open[0];
  const later = open.filter((x) => x !== current);
  return (
    <>
      {current && <StopCard key={current.orderId} stop={current} opsPhone={opsPhone} onDone={onDone} />}
      {later.length > 0 && <div className="px-1 text-sm text-muted-foreground">{T.later(later.length)}</div>}
      {later.map((x) => (
        <button key={x.orderId} className="flex min-h-[56px] w-full items-center justify-between rounded-lg border bg-card px-3 text-left" onClick={() => setChosen(x.orderId)}>
          <span>
            <span className="font-semibold">{x.orderId}</span>
            <span className="text-sm text-muted-foreground"> · {x.area ?? x.zone}</span>
            {x.deliverBy && <span className="text-sm text-muted-foreground"> · {T.by(dhakaClock(x.deliverBy))}</span>}
          </span>
          <span>{x.collectPaisa > 0 ? takaBn(x.collectPaisa) : T.paid}</span>
        </button>
      ))}
      {done.length > 0 && (
        <>
          <div className="px-1 text-sm text-muted-foreground">{T.doneList(done.length)}</div>
          <div className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-muted-foreground">
            <span>✓ {done.map((x) => x.orderId).join(", ")}</span>
            <span>{takaBn(done.reduce((sum, x) => sum + x.collectPaisa, 0))}</span>
          </div>
        </>
      )}
    </>
  );
}

function StopCard({ stop, opsPhone, onDone }: { stop: RiderStop; opsPhone: string | null; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [failing, setFailing] = useState(false);
  const [called, setCalled] = useState(() => calledSet().has(stop.orderId));
  const codeRef = useRef<HTMLInputElement>(null);
  const cash = stop.collectPaisa > 0;

  const deliver = useMutation({
    mutationFn: (c: string) => api(`/rider/orders/${stop.orderId}/deliver`, { body: { code: c, cashCollected: cash } }),
    onSuccess: () => {
      setMessage(null);
      onDone();
    },
    onError: (err) => {
      setMessage(riderError(err));
      setCode("");
      if (err instanceof ApiError && (err.code === "code_locked" || err.code === "not_out_for_delivery" || err.code === "order_changed")) onDone();
    },
  });
  const fail = useMutation({
    mutationFn: (reason: string) => api(`/rider/orders/${stop.orderId}/fail`, { body: { reason } }),
    onSuccess: () => onDone(),
    onError: (err) => setMessage(riderError(err)),
  });

  // A prepaid order goes as soon as the fourth digit is in; a cash one waits for the hold.
  useEffect(() => {
    if (code.length === 4 && !cash && !deliver.isPending) deliver.mutate(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const deadline = stop.deliverBy ? new Date(stop.deliverBy).getTime() : null;
  const minutesLeft = deadline === null ? null : Math.round((deadline - Date.now()) / 60_000);

  return (
    <Card className="border-2 border-primary">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{T.next}</Badge>
          <span className="text-xl font-semibold">{stop.orderId}</span>
          <span className="text-sm text-muted-foreground">{T.items(stop.items)}</span>
          {stop.deliverBy && (
            <span className={`ml-auto text-sm ${minutesLeft !== null && minutesLeft < 30 ? "font-semibold text-destructive" : "text-muted-foreground"}`}>
              {minutesLeft !== null && minutesLeft < 0 ? T.late : T.by(dhakaClock(stop.deliverBy))}
            </span>
          )}
        </div>
        <div className="text-2xl font-bold">{cash ? `${T.collect}: ${takaBn(stop.collectPaisa)}` : T.paid}</div>
        {stop.address && <div>{stop.address}</div>}
        <div className="grid grid-cols-2 gap-2">
          {stop.phone && (
            <a
              href={`tel:${stop.phone}`}
              onClick={() => {
                markCalled(stop.orderId);
                setCalled(true);
              }}
            >
              <Button variant="outline" className="big-tap w-full">
                {T.call}
              </Button>
            </a>
          )}
          {stop.address && (
            <a href={mapUrl(stop.address)} target="_blank" rel="noreferrer">
              <Button variant="outline" className="big-tap w-full">
                {T.map}
              </Button>
            </a>
          )}
        </div>

        {stop.codeLocked ? (
          <div className="space-y-2">
            <p className="text-destructive">{T.locked}</p>
            {opsPhone && (
              <a href={`tel:${opsPhone}`} className="block">
                <Button variant="destructive" className="big-tap w-full">
                  {T.callOps}
                </Button>
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm" htmlFor={`code-${stop.orderId}`}>
              {T.code}
            </label>
            <div className="relative mx-auto flex w-fit gap-2" onClick={() => codeRef.current?.focus()}>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`flex h-16 w-14 items-center justify-center rounded-md border-2 text-3xl font-bold ${i === code.length ? "border-primary" : "border-input"}`}
                >
                  {code[i] ? bnDigits(code[i]!) : ""}
                </div>
              ))}
              <input
                id={`code-${stop.orderId}`}
                ref={codeRef}
                className="absolute inset-0 h-full w-full cursor-text opacity-0"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={4}
                value={code}
                disabled={deliver.isPending}
                onChange={(e) => {
                  setMessage(null);
                  setCode(e.target.value.replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d))).replace(/\D/g, "").slice(0, 4));
                }}
              />
            </div>
            {cash ? (
              <HoldButton label={T.holdCash(stop.collectPaisa)} disabled={code.length !== 4 || deliver.isPending} onHeld={() => deliver.mutate(code)} />
            ) : (
              deliver.isPending && <p className="text-center text-muted-foreground">{T.sending}</p>
            )}
          </div>
        )}
        {message && <p className="text-destructive">{message}</p>}

        {failing ? (
          <div className="grid grid-cols-1 gap-2">
            {!called && <p className="text-sm text-destructive">{T.callFirst}</p>}
            {T.reasons.map(([reason, label]) => (
              <Button
                key={reason}
                variant="destructive"
                className="big-tap"
                disabled={!called || fail.isPending}
                onClick={() => window.confirm(T.confirmFail(stop.orderId, label)) && fail.mutate(reason)}
              >
                {label}
              </Button>
            ))}
            <Button variant="ghost" className="big-tap" onClick={() => setFailing(false)}>
              {T.back}
            </Button>
          </div>
        ) : (
          <button className="w-full py-2 text-center text-muted-foreground underline" onClick={() => setFailing(true)}>
            {T.cantDeliver}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

/** A button that fires only after being held for a second, so a brush of the thumb cannot confirm cash. */
function HoldButton({ label, disabled, onHeld }: { label: string; disabled: boolean; onHeld: () => void }) {
  const HOLD_MS = 1000;
  const [progress, setProgress] = useState(0);
  const timer = useRef<number | null>(null);
  const start = useRef(0);
  const stop = () => {
    if (timer.current !== null) cancelAnimationFrame(timer.current);
    timer.current = null;
    setProgress(0);
  };
  const tick = () => {
    const p = Math.min(1, (performance.now() - start.current) / HOLD_MS);
    setProgress(p);
    if (p >= 1) {
      stop();
      if (navigator.vibrate) navigator.vibrate(60);
      onHeld();
    } else timer.current = requestAnimationFrame(tick);
  };
  useEffect(() => stop, []);
  return (
    <button
      type="button"
      disabled={disabled}
      className="big-tap relative w-full select-none overflow-hidden rounded-md bg-primary font-semibold text-primary-foreground disabled:opacity-50"
      style={{ touchAction: "none" }}
      onPointerDown={(e) => {
        if (disabled) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = performance.now();
        timer.current = requestAnimationFrame(tick);
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="absolute inset-y-0 left-0 bg-black/20" style={{ width: `${progress * 100}%` }} aria-hidden="true" />
      <span className="relative">{label}</span>
    </button>
  );
}
